import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { planAccessOf, resolveEntitlementState, subscriptionRestrictionCopy, type EffectiveSubscriptionState } from '../types';

const DEMO_FEATURES = {
  'priority-report-access': { title: 'Priority Report Access (illustrative example)', callKey: 'priority-report-access' as const },
  'advanced-analytics-access': { title: 'Advanced Analytics Access (illustrative example)', callKey: 'advanced-analytics-access' as const },
} as const;

type DemoKey = keyof typeof DEMO_FEATURES;

/**
 * Proves "deep-linking to a locked page shows an upgrade-required page and never loads protected data" —
 * a real route (`/subscription-demo/:featureKey`), not just a hidden button. The order matters: this page
 * ALWAYS resolves readiness + entitlement from `listFeatures`/`getCurrentSubscription` FIRST, and only
 * calls the actual protected endpoint (`callDemoFeature`) once both have already passed. Typing this URL
 * directly, with no prior navigation through the plan-comparison table, produces the identical
 * Coming-Soon/Locked/Enabled outcome the in-app padlock would have shown — because the same
 * `RequireFeatureEntitlementFilter` on the backend is the actual boundary, not this page's own check.
 */
export function DemoFeaturePage() {
  const { featureKey } = useParams<{ featureKey: string }>();
  const [state, setState] = useState<'loading' | 'coming-soon' | 'locked' | 'subscription-required' | 'enabled' | 'not-found' | 'error'>('loading');
  const [subscriptionState, setSubscriptionState] = useState<EffectiveSubscriptionState>('NoSubscription');
  const [requiredPlanName, setRequiredPlanName] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      const demo = featureKey && featureKey in DEMO_FEATURES ? DEMO_FEATURES[featureKey as DemoKey] : null;
      if (!demo) {
        if (!cancelled) setState('not-found');
        return;
      }

      try {
        const [features, subscription] = await Promise.all([
          subscriptionGateway.listFeatures(),
          subscriptionGateway.getCurrentSubscription(),
        ]);
        if (cancelled) return;

        const feature = features.find((f) => f.featureKey === demo.callKey);
        if (!feature) {
          setState('coming-soon');
          return;
        }

        // Same single rule every plan-gated control uses (readiness → subscription in force → plan tier).
        const access = planAccessOf(subscription);
        const display = resolveEntitlementState(feature, access);
        if (display === 'coming-soon' || display === 'pilot-only' || display === 'unavailable') {
          setState('coming-soon');
          return;
        }
        if (display === 'subscription-required') {
          setSubscriptionState(access.effectiveState);
          setState('subscription-required');
          return; // Protected data is NEVER requested past this point.
        }
        if (display === 'locked') {
          setRequiredPlanName(feature.minimumPlanName);
          setState('locked');
          return; // Protected data is NEVER requested past this point.
        }

        // Only now — after readiness and entitlement both passed — does this page call the actual
        // backend-gated endpoint.
        const result = await subscriptionGateway.callDemoFeature(demo.callKey);
        if (cancelled) return;
        setMessage(result.message);
        setState('enabled');
      } catch (err) {
        if (!cancelled) {
          setError(friendlyErrorMessage(err, 'Could not load this feature.'));
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [featureKey]);

  const title = featureKey && featureKey in DEMO_FEATURES ? DEMO_FEATURES[featureKey as DemoKey].title : 'Feature';

  if (state === 'loading') {
    return <section className="card card--wide"><p className="muted">Loading…</p></section>;
  }

  if (state === 'not-found') {
    return <section className="card card--wide"><h2>Not found</h2><p className="muted">No such example feature.</p></section>;
  }

  if (state === 'coming-soon') {
    return (
      <section className="card card--wide">
        <h2>{title}</h2>
        <span className="entitlement-badge entitlement-badge--coming-soon" role="status"><span aria-hidden="true">◷</span> Coming soon</span>
        <p className="muted" style={{ marginTop: 12 }}>This capability isn't available in this environment yet, on any plan.</p>
      </section>
    );
  }

  if (state === 'subscription-required') {
    const copy = subscriptionRestrictionCopy(subscriptionState);
    return (
      <section className="card card--wide">
        <h2>{title}</h2>
        <span className="entitlement-badge entitlement-badge--locked" role="status"><span aria-hidden="true">🔒</span> {copy.title}</span>
        <p className="muted" style={{ marginTop: 12 }}>{copy.message} No protected data was requested for this page.</p>
        <a href="/settings/subscription">View plans →</a>
      </section>
    );
  }

  if (state === 'locked') {
    return (
      <section className="card card--wide">
        <h2>{title}</h2>
        <span className="entitlement-badge entitlement-badge--locked" role="status"><span aria-hidden="true">🔒</span> Requires {requiredPlanName ?? 'a higher plan'}</span>
        <p className="muted" style={{ marginTop: 12 }}>
          This feature is real and working, but not part of your current plan. No protected data was
          requested for this page.
        </p>
        <a href="/settings/subscription">Upgrade to {requiredPlanName ?? 'a higher plan'} →</a>
      </section>
    );
  }

  if (state === 'error') {
    return <section className="card card--wide"><h2>{title}</h2><p className="error">{error}</p></section>;
  }

  return (
    <section className="card card--wide">
      <h2>{title}</h2>
      <p className="success">{message}</p>
    </section>
  );
}
